import * as React from 'react';
import { VisitorFeatureBoundary } from '@/components/tenancy/visitor-feature-boundary';
import {
  ApiError, getGetMyPolicyRequirementsQueryKey, getGetVisitorPolicyRequirementsQueryKey,
  useGetMyPolicyRequirements, useGetVisitorPolicyRequirements,
  useAcceptMyPolicyRequirements, useAcceptVisitorPolicyRequirements,
  type PolicyRequirements, type PolicyAdmissionIntent
} from '@cdf/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePolicyAdministrationContext, invalidatePolicyAdministration, uncertainPolicyWrite } from '@/hooks/use-policy-administration-context';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getErrorMessage } from '@/lib/utils';
import { formatDate } from '@/lib/site-time';

export type PolicyAcceptanceSheetProps = {
  intent: PolicyAdmissionIntent;
  open: boolean;
  spaceId: string | null;
  locationName?: string | null;
  approvalId?: string;
  /** Booking entry points skip an empty review without writing acceptance evidence. */
  skipEmpty?: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void | Promise<void>;
};

export function PolicyAcceptanceSheet(props: PolicyAcceptanceSheetProps) {
  return props.approvalId
    ? <VisitorFeatureBoundary><ScopedPolicyAcceptanceSheet {...props} /></VisitorFeatureBoundary>
    : <ScopedPolicyAcceptanceSheet {...props} />;
}

function ScopedPolicyAcceptanceSheet(props: PolicyAcceptanceSheetProps) {
  const context = usePolicyAdministrationContext();
  return <Acceptance key={context.key + ':' + props.spaceId + ':' + (props.approvalId ?? '') + ':' + props.open}
    {...props} context={context} />;
}

function Acceptance({ intent, open, spaceId, locationName, approvalId, skipEmpty = false, onOpenChange, onCompleted, context }:
  PolicyAcceptanceSheetProps & { context: ReturnType<typeof usePolicyAdministrationContext> }) {
  const client = useQueryClient();
  const params = { spaceId: spaceId ?? '' };
  const member = useGetMyPolicyRequirements(params, {
    query: { queryKey: [...getGetMyPolicyRequirementsQueryKey(params), context.key], enabled: open && !!spaceId && !approvalId && context.ready, retry: false, staleTime: 0 },
    request: context.request
  });
  const visitor = useGetVisitorPolicyRequirements(approvalId ?? '', params, {
    query: { queryKey: [...getGetVisitorPolicyRequirementsQueryKey(approvalId ?? '', params), context.key],
      enabled: open && !!spaceId && !!approvalId, retry: false, staleTime: 0 },
    request: context.request
  });
  const query = approvalId ? visitor : member;
  const memberAccept = useAcceptMyPolicyRequirements({ mutation: { retry: false }, request: context.request });
  const visitorAccept = useAcceptVisitorPolicyRequirements({ mutation: { retry: false }, request: context.request });
  const [review, setReview] = React.useState<PolicyRequirements | null>(null);
  const [checked, setChecked] = React.useState<Set<string>>(() => new Set());
  const [error, setError] = React.useState<string | null>(null);
  const [locked, setLocked] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const pending = React.useRef(false);
  const emptyHandled = React.useRef(false);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  React.useEffect(() => {
    if (!review && query.data) setReview(query.data);
  }, [review, query.data]);
  const stale = !!review && !!query.data && (query.data.revision !== review.revision ||
    query.data.rules.some(r => review.rules.find(old => old.id === r.id)?.needsAcceptance !== r.needsAcceptance));
  const missing = review?.rules.filter(rule => rule.needsAcceptance) ?? [];
  const allChecked = missing.every(rule => !rule.requiresIndividualAcknowledgement || checked.has(rule.id));
  const ready = !!spaceId && (!!approvalId || context.ready);

  async function refresh() {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      const current = await query.refetch();
      if (current.isError || !current.data) throw current.error ?? new Error('Requirements are unavailable.');
      if (!mounted.current) return;
      setReview(current.data); setChecked(new Set());
      // A read can confirm an uncertain acceptance; it never repeats the write.
      setSaved(current.data.complete);
      setLocked(false);
    } catch (err) { if (mounted.current) setError(getErrorMessage(err)); }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  }

  async function finish() {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try {
      if (!(skipEmpty && review?.complete && review.rules.length === 0)) await invalidatePolicyAdministration(client);
      if (!mounted.current) return;
      await onCompleted?.();
      if (mounted.current) onOpenChange(false);
    } catch (err) {
      if (mounted.current) setError((skipEmpty && review?.rules.length === 0
        ? 'Requirements could not be checked. Refresh before continuing. '
        : 'Acceptance is saved. Refresh the requirements before returning. ') + getErrorMessage(err));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  }

  async function accept() {
    if (pending.current || !review || !allChecked || locked || stale || query.isError || query.isFetching || !ready) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      const data = { spaceId: review.spaceId, revision: review.revision,
        rules: review.rules.map(rule => ({ ruleId: rule.id, version: rule.version })),
        checkedRuleIds: missing.filter(rule => rule.requiresIndividualAcknowledgement && checked.has(rule.id)).map(rule => rule.id) };
      const result = approvalId
        ? await visitorAccept.mutateAsync({ id: approvalId, data })
        : await memberAccept.mutateAsync({ data });
      if (!mounted.current) return;
      setReview(result); setSaved(true); setChecked(new Set());
      try { await invalidatePolicyAdministration(client); }
      catch { if (mounted.current) setError('Acceptance is saved. Refresh to check the latest requirements.'); }
    } catch (err) {
      if (!mounted.current) return;
      setError(uncertainPolicyWrite(err)
        ? 'The acceptance outcome could not be confirmed. Refresh to check it before trying again.'
        : err instanceof ApiError && err.status === 409
          ? 'Policies and standards changed. Refresh and review them before accepting.'
          : getErrorMessage(err));
      setLocked(true);
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  }

  const empty = !!review && review.complete && review.rules.length === 0;
  React.useEffect(() => {
    if (!skipEmpty || !open || !ready || !empty || query.isError || query.isFetching ||
      stale || locked || emptyHandled.current) return;
    emptyHandled.current = true;
    void finish();
  });

  if (skipEmpty && open && ready && !error && !query.isError && (query.isPending || !review || empty)) return null;
  return <Sheet open={open} onOpenChange={value => { if (!pending.current) onOpenChange(value); }}>
    <SheetContent side="bottom"
      className="mx-auto flex max-h-[92dvh] w-full max-w-3xl flex-col gap-0 rounded-t-2xl p-0"
      onEscapeKeyDown={event => { if (pending.current) event.preventDefault(); }}
      onPointerDownOutside={event => { if (pending.current) event.preventDefault(); }}>
      <SheetHeader className="border-b px-5 pt-5 pb-4 pr-12">
        <SheetTitle>Policies and standards</SheetTitle>
        <SheetDescription>{locationName || 'Requirements for your selected space'}. Accept for yourself before {intent}.</SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {!ready ? <p role="alert">Select a space in an entity you can access before reviewing requirements.</p> :
          query.isPending ? <p role="status">Loading requirements…</p> :
          query.isError ? <p role="alert" className="text-destructive">{getErrorMessage(query.error)}</p> : null}
        {stale && <p role="alert">Requirements changed. Refresh and review before continuing.</p>}
        {review && <ul className="space-y-3">{review.rules.map(rule => <li key={rule.id} className="rounded-lg border p-3">
          <h3 className="font-medium">{rule.title}</h3>
          <p className="whitespace-pre-wrap text-sm">{rule.body}</p>
          {!rule.needsAcceptance ? <p className="text-muted-foreground mt-2 text-xs">Already accepted{rule.acceptance?.expiresAt ? ' · valid until ' + formatDate(rule.acceptance.expiresAt, { kind: 'device' }) : ''}</p> :
            rule.requiresIndividualAcknowledgement ? <label htmlFor={`policy-ack-${rule.id}`} className="mt-3 flex items-start gap-2 text-sm">
              <Checkbox id={`policy-ack-${rule.id}`} className="mt-0.5" disabled={busy || locked || stale} checked={checked.has(rule.id)}
                onCheckedChange={value => { const selected = value === true; setChecked(previous => {
                  const next = new Set(previous); if (selected) next.add(rule.id); else next.delete(rule.id); return next;
                }); }} />
              <span>I acknowledge {rule.title}</span>
            </label> : null}
        </li>)}</ul>}
        {review?.rules.length === 0 && <p>No Policies and standards apply to this space.</p>}
        {saved && <p role="status">Acceptance is current. Review and confirm your {intent} again.</p>}
        {error && <p role="alert" className="text-destructive">{error}</p>}
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button variant="outline" disabled={!ready || busy} onClick={() => void refresh()}>Refresh requirements</Button>
        {saved || review?.complete ? <Button disabled={busy || stale || query.isError || query.isFetching} onClick={() => void finish()}>Return to {intent}</Button> :
          <Button disabled={!review || !ready || !allChecked || busy || locked || stale || query.isError || query.isFetching}
            onClick={() => void accept()}>{busy ? 'Saving…' : 'Accept'}</Button>}
      </div>
    </SheetContent>
  </Sheet>;
}
